import { Core, Decorative } from "@faire/design-tokens";
import {
  ISuggestionEntry,
  ISuggestionEntryMixin,
} from "@faire/retailer-visitor-shared/components/TopSearch/ResultDropdown/__internal__/Interfaces";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { Avatar } from "@faire/web--source/slate/Avatar";
import { TrophyIcon } from "@faire/web--source/slate/icons/Trophy";
import { Column } from "@faire/web--source/slate/Layout";
import { getSpacing, Spacer } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web--source/slate/Typography";
import { Link } from "@faire/web--source/ui/LinkAnchor";
import React from "react";
import styled, { css } from "styled-components";

interface IProps {
  suggestion: ISuggestionEntryMixin;
  selected: boolean;
  onClick: (suggestion: ISuggestionEntry, navigate?: boolean) => void;
  onHover: () => void;
  onMouseLeave: () => void;
}

export const ResultDropdownImageIconItem: React.FC<IProps> = ({
  suggestion,
  selected,
  onClick,
  onHover,
  onMouseLeave,
}) => {
  const { strictLocalize } = useStrictLocalization();
  const { name, is_top_shop, profile_image } = suggestion.searchData;
  return (
    <li>
      <Wrapper
        to={suggestion.url}
        onClick={() => onClick(suggestion)}
        onMouseEnter={onHover}
        onMouseLeave={onMouseLeave}
        $selected={selected}
      >
        <Spacer
          width={{
            mobileAndAbove: "2x",
            tabletAndAbove: 4,
          }}
        />
        <Column>
          <Spacer height="1x" />
          <StyledAvatar
            size="smaller"
            altTag={strictLocalize({
              defaultMessage: "Brand logo image",
              description: { text: "Alt text for brand logo image" },
            })}
            $isTopShop={is_top_shop}
            iconData={
              is_top_shop
                ? {
                    position: "bottomRight",
                    icon: (
                      <TrophyIcon
                        color={Decorative.icon.green}
                        fill={Decorative.icon.green}
                        width="15"
                        height="15"
                        active
                      />
                    ),
                  }
                : undefined
            }
            image={profile_image}
          />
          <Spacer height="1x" />
        </Column>
        <Spacer width={{ mobileAndAbove: "2x", tabletAndAbove: "1x" }} />
        <MaxWidthLabel>{name}</MaxWidthLabel>
        <Spacer width="2x" />
      </Wrapper>
    </li>
  );
};

const StyledAvatar = styled(Avatar)<{ $isTopShop?: boolean }>`
  ${({ $isTopShop }) =>
    $isTopShop
      ? css`
          border: 2px solid ${Decorative.icon.green};
          border-radius: 50%;
        `
      : ""}
`;

const Wrapper = styled(Link)<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  cursor: pointer;
  background-color: ${({ $selected }) =>
    $selected ? Core.surface.subdued : null};
  ${responsiveMediaQueries.tabletAndAbove} {
    border-radius: ${getSpacing("5x")};
  }
`;

const MaxWidthLabel = styled(Typography)`
  max-width: ${getSpacing(207)};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
